/* global process */
import * as Sentry from "@sentry/remix";
import { thirdPartyErrorFilterIntegration } from "@sentry/browser";
/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://7d4fab9ce3d3162293030c5fa7a0cca3@o1276829.ingest.us.sentry.io/4507146073997312",
    tracesSampleRate: 0.1,

    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      // Filter out errors from third-party scripts (browser extensions, analytics, etc.)
      thirdPartyErrorFilterIntegration({
        filterKeys: ["pendora-digital-downloads"],
        behaviour: "drop-error-if-contains-third-party-frames",
      }),
    ],

    // Filter out third-party errors (browser extensions, external scripts)
    ignoreErrors: [
      // Grammarly extension errors
      "ac_wordsDistributor",
      /ac_\w+/,
      /Grammarly/i,
      // Common browser extension patterns
      "ResizeObserver loop",
      "Non-Error promise rejection captured",
      "Script error.",
      // Chrome extension API noise — `chrome.runtime.sendMessage` can only be
      // called from extension contexts. "Tab not found" fires when an
      // extension (password manager, ad blocker, translator, etc.) tries to
      // message a tab that's already closed. Our app never calls this API,
      // so any occurrence is third-party. Anchored regex covers wording
      // variations across Chrome versions.
      "Invalid call to runtime.sendMessage(). Tab not found.",
      /runtime\.sendMessage.*Tab not found/i,
      // Extension URL patterns
      /chrome-extension/,
      /moz-extension/,
      /safari-extension/,
      // Other noisy patterns
      /adblock/i,
      /Request timeout/,
      // Distributor errors (translation/language extensions)
      /Distributor/,
      /userDistributor/,
      /availableLanguagesDistributor/,
      // Safari/iOS webkit messageHandlers (browser extensions injecting code)
      /webkit\.messageHandlers/,
      /messageHandlers\[.*\]\.postMessage/,
      "window.webkit.messageHandlers",
      // In-app browser WKWebView (Shopify mobile app, Instagram, FB Messenger,
      // etc.) — host app doesn't handle native postMessage bridges from the
      // embedded page. Not actionable from our code.
      "WKWebView API client did not respond to this postMessage",
      /WKWebView.*postMessage/i,
      // Minified unhandled rejection seen on iOS Safari co-incident with
      // `Axiom tracking failed: Load failed` warnings. Source hardened in
      // trackAxiomEvent (defensive try/catch on rejection access); keep the
      // exact-match filters as belt-and-suspenders so any straggler post-deploy
      // doesn't re-fire Slack alerts. Anchored regex — won't mask other errors.
      /^Zd$/,
      /^Error: Zd$/,
      // Microsoft Clarity analytics errors
      /Ma\[l\]\.children/,
      /clarity/i,
      "Unexpected Server Error",
      /hxp.*suppression/i,
      /hxpChatSuppression/,
      // Browser CSS feature detection error (older browsers not supporting a queried feature, e.g. hover, pageContext)
      /feature named.*was not found/,
      // Corporate proxy (Zscaler etc.) intercepting loader requests with HTML block pages
      /Zscaler/i,
      /<!DOCTYPE/i,
      /Unexpected token '<'/,
      /Unexpected token <, "<!DOCTYPE/i,
      // IE Mark-of-the-Web HTML pages — corporate firewalls / proxies (e.g.
      // Forcepoint, McAfee Web Gateway, generic IIS block pages) serve a
      // static HTML response that begins with `<!-- saved from url=(0014)about:internet -->`
      // when intercepting our Remix loader/action requests. Remix can't
      // parse the response as JSON and bubbles the body up as Error.message,
      // which doesn't start with `<!DOCTYPE` so the filter above misses it.
      /saved from url=\(\d+\)about:internet/i,
      // Belt-and-suspenders: any error whose body is an inline HTML block
      // page starting with a comment or <html>. Anchored to start-of-string
      // so legitimate errors that merely mention `<html>` in a stack frame
      // aren't masked.
      /^<!--\s*saved from url=/i,
      /^<html[\s>]/i,
    ],

    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^ms-browser-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],

    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter non-Error rejections from third-party code
      if (typeof error === "string") {
        if (
          error.includes("Distributor") ||
          error.includes("Request timeout") ||
          error.includes("extension")
        ) {
          return null;
        }
      }

      if (error instanceof Error && error.message === "Unexpected Server Error") {
        const breadcrumbs = event.breadcrumbs || [];
        const hasNativeBridgeActivity = breadcrumbs.some(
          (bc) =>
            bc.message?.includes("hxp") ||
            bc.message?.includes("messageHandlers") ||
            bc.message?.includes("webkit") ||
            bc.message?.includes("native")
        );
        if (hasNativeBridgeActivity) {
          return null;
        }

        const hasNoStackTrace = !event.exception?.values?.[0]?.stacktrace?.frames?.length;
        if (hasNoStackTrace) {
          return null;
        }
      }

      if (event.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        const isFromExtension = frames.some(
          (frame) =>
            frame.filename &&
            (/^(chrome|moz|safari|ms-browser)-extension:\/\//.test(
              frame.filename
            ) ||
              frame.filename.includes("extension"))
        );
        if (isFromExtension) {
          return null;
        }
      }

      return event;
    },
  });
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>
  );
});
